import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { usePsychAssistSession } from "../hooks/usePsychAssistSession";

export type ERState = "steadfast" | "poised" | "tilted";
export type InterventionLevel = "none" | "visual" | "voice" | "lockout";

interface ERSnapshot {
  score: number;
  state: ERState;
  timestamp: Date;
}

interface OvertradingStatus {
  isOvertrading: boolean;
  tradesInWindow: number;
  warning?: string;
}

interface SentimentResult {
  sentiment: number;
  confidence: number;
  keywords: string[];
  tiltIndicators: string[];
  summary: string;
}

interface ERContextValue {
  isMonitoring: boolean;
  erScore: number;
  resonanceState: ERState;
  sessionId: number | null;
  overtradingStatus: OvertradingStatus | null;
  analyser: AnalyserNode | null;
  timeInTiltSeconds: number;
  infractionCount: number;
  maxTiltScore: number;
  sessionStartTime: number | null;
  recentInfraction: boolean;
  lastInfractionAt: number | null;
  interventionLevel: InterventionLevel;
  isLockedOut: boolean;
  lastSentiment: SentimentResult | null;
  vadActive: boolean;
  startMonitoring: () => Promise<void>;
  stopMonitoring: () => Promise<void>;
  updateScore: (delta: number) => void;
  addInfraction: (keywords: string[]) => void;
  clearRecentInfraction: () => void;
  dismissLockout: () => void;
  getRecentSnapshots: () => ERSnapshot[];
}

const ERContext = createContext<ERContextValue | null>(null);

export function useER() {
  const context = useContext(ERContext);
  if (!context) throw new Error("useER must be used within an ERProvider");
  return context;
}

export function useERSafe() {
  return useContext(ERContext);
}

interface ERProviderProps {
  children: React.ReactNode;
}

function stateFor(score: number): ERState {
  if (score > 0.5) return "steadfast";
  if (score < -0.5) return "tilted";
  return "poised";
}

function interventionFor(score: number): InterventionLevel {
  if (score <= -5) return "lockout";
  if (score <= -3) return "voice";
  if (score <= -1) return "visual";
  return "none";
}

export function ERProvider({ children }: ERProviderProps) {
  const psych = usePsychAssistSession();
  const [dismissedLockout, setDismissedLockout] = useState(false);
  const score = psych.currentScore ?? 0;
  const resonanceState = stateFor(score);
  const interventionLevel = interventionFor(score);
  const sessionStartedAt = psych.session?.startedAt
    ? new Date(psych.session.startedAt).getTime()
    : null;

  const startMonitoring = useCallback(async () => {
    setDismissedLockout(false);
    await psych.start();
  }, [psych]);

  const stopMonitoring = useCallback(async () => {
    await psych.finalize("user_stopped");
  }, [psych]);

  const value = useMemo<ERContextValue>(
    () => ({
      isMonitoring: psych.isActive,
      erScore: score,
      resonanceState,
      sessionId: psych.session?.sessionId ?? null,
      overtradingStatus: null,
      analyser: null,
      timeInTiltSeconds: 0,
      infractionCount: 0,
      maxTiltScore: Math.min(0, score),
      sessionStartTime: sessionStartedAt,
      recentInfraction: false,
      lastInfractionAt: null,
      interventionLevel,
      isLockedOut: interventionLevel === "lockout" && !dismissedLockout,
      lastSentiment: null,
      vadActive: false,
      startMonitoring,
      stopMonitoring,
      updateScore: () => undefined,
      addInfraction: () => undefined,
      clearRecentInfraction: () => undefined,
      dismissLockout: () => setDismissedLockout(true),
      getRecentSnapshots: () =>
        psych.session
          ? [
              {
                score,
                state: resonanceState,
                timestamp: new Date(psych.session.startedAt),
              },
            ]
          : [],
    }),
    [
      dismissedLockout,
      interventionLevel,
      psych.isActive,
      psych.session,
      resonanceState,
      score,
      sessionStartedAt,
      startMonitoring,
      stopMonitoring,
    ],
  );

  return <ERContext.Provider value={value}>{children}</ERContext.Provider>;
}
